import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import {
  fetchSubscriptionUsagePeriods,
  type FetchSubscriptionUsagePeriodsParams,
} from '../services/subscriptions';
import type { SubscriptionUsagePeriodSummary } from '../types';

interface UseSubscriptionUsagePeriodsOptions extends FetchSubscriptionUsagePeriodsParams {
  enabled?: boolean;
  permissionCode?: string;
}

/**
 * Load canonical subscription usage periods when permitted.
 */
export function useSubscriptionUsagePeriods(options: UseSubscriptionUsagePeriodsOptions = {}) {
  const {
    enabled = true,
    permissionCode = 'subscriptions_read',
    tenantId,
    subscriptionId,
    status,
    periodStartFrom,
    periodStartTo,
    periodEndFrom,
    periodEndTo,
    ordering,
  } = options;
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canRead = useMemo(() => hasPermission(permissionCode), [hasPermission, permissionCode]);
  const [usagePeriods, setUsagePeriods] = useState<SubscriptionUsagePeriodSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadUsagePeriods = useCallback(async () => {
    if (!enabled || !canRead) {
      setUsagePeriods([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchSubscriptionUsagePeriods({
        tenantId,
        subscriptionId,
        status,
        periodStartFrom,
        periodStartTo,
        periodEndFrom,
        periodEndTo,
        ordering,
      });
      setUsagePeriods(data);
      setError(null);
    } catch (err) {
      setUsagePeriods([]);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [
    canRead,
    enabled,
    ordering,
    periodEndFrom,
    periodEndTo,
    periodStartFrom,
    periodStartTo,
    status,
    subscriptionId,
    tenantId,
  ]);

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (!enabled || !canRead) {
      setUsagePeriods([]);
      setError(null);
      setLoading(false);
      return;
    }
    loadUsagePeriods();
  }, [canRead, enabled, loadUsagePeriods, permissionsLoading]);

  return {
    usagePeriods,
    isLoading: loading,
    error,
    refresh: loadUsagePeriods,
    isReady: enabled && canRead && !permissionsLoading,
  };
}
